// PetList.tsx

import {
  Alert,
  Box,
  Card,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material'
import { useMemo, useState } from 'react'
import {
  ListContextProvider,
  Pagination,
  useGetIdentity,
  useGetList,
  useGetOne,
  useTranslate,
} from 'react-admin'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  parseAssignedVetFilterFromSearch,
  parseVetFilterFromSearch,
  petAssignedToVet,
  petMatchesVetClinic,
} from '../clinics/clinicAssociation'
import { DataTruncationNotice } from '../components/DataTruncationNotice'
import PageHeader from '../components/PageHeader'
import { useClinicLinkedPets } from '../hooks/useClinicLinkedPets'
import { useDeviceLastReadings } from '../hooks/useDeviceLastReadings'
import { useDevicesWithMetrics } from '../hooks/useDevicesWithMetrics'
import type { Device, Pet, User } from '../types'
import GlobalSearchBox from './GlobalSearchBox'
import HiddenPetsPanel from './HiddenPetsPanel'
import PetDatagrid from './PetDatagrid'

/**
 * Custom PetList that:
 * 1. When a Clinic Directory vet filter is active, uses the backend
 *    /users/clinic-linked-pets endpoint as the canonical pet set — the
 *    SAME source the directory's "Linked pets: N" count is derived from.
 *    This is what makes count and list agree.
 * 2. Surfaces pets that are linked but data-less (no device, waiting sync)
 *    instead of silently hiding them, so the clinic can see what state each
 *    pet is in. (Previously these pets were dropped, which made the
 *    directory's count look wrong.)
 * 3. Sorts by latest device activity when known; otherwise by createdAt.
 */
const PetList = () => {
  const translate = useTranslate()
  const navigate = useNavigate()
  const location = useLocation()
  const { identity } = useGetIdentity()
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(25)
  const [searchQuery, setSearchQuery] = useState('')
  const [showHidden, setShowHidden] = useState(false)
  // Backend gate (clinicHidden.ts, ada-core) is role EXACTLY 'vet' — a
  // vetadmin's visibility doesn't come from the master/sub clinic-hide
  // circle, so it 403s there. Same gate HidePetButton already uses.
  const canShowHidden = identity?.role === 'vet'
  // Read the vet filter from react-router's parsed location.search rather
  // than window.location.search: react-admin runs under HashRouter so the
  // browser-level search is empty and the actual query string lives inside
  // the hash. window.location.search returned '' and silently disabled the
  // filter — which is the bug the Clinic Directory "View Pets" jump kept
  // hitting (filter never applied, list showed every pet).
  const clinicVetFilter = useMemo(
    () => parseVetFilterFromSearch(location.search),
    [location.search],
  )

  // Clinic Team "View Patients" deep-link — `?filter={"assignedVet":N}`.
  // Independent of clinicVetFilter and read from a DIFFERENT field: this one
  // asks who is RESPONSIBLE (pets.assigned_vet_id), not who has access
  // (owner.vets). A sub-account is normally absent from owner.vets, so the two
  // are not interchangeable. Same hash-router caveat as above.
  const assignedVetFilter = useMemo(
    () => parseAssignedVetFilterFromSearch(location.search),
    [location.search],
  )

  // Fetch the clinic's display name for the active-filter chip. Skipped
  // when no vet filter is active — useGetOne short-circuits on enabled=false.
  const { data: clinicUser } = useGetOne<User>(
    'users',
    { id: clinicVetFilter ?? 0 },
    { enabled: clinicVetFilter !== null },
  )

  // Fetch the assigned vet's display name for the active-filter chip.
  const { data: assignedVetUser } = useGetOne<User>(
    'users',
    { id: assignedVetFilter ?? 0 },
    { enabled: assignedVetFilter !== null },
  )

  const clearClinicFilter = () => {
    // Strip the filter query param while keeping any other search keys.
    const params = new URLSearchParams(location.search)
    params.delete('filter')
    const query = params.toString()
    navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true })
  }
  const { devicesWithMetrics, isLoading: metricsCheckLoading } = useDevicesWithMetrics()
  // Window-independent per-device last reading. `devicesWithMetrics` only says a
  // device has EVER reported, so it cannot answer "is this sensor still live" —
  // that is what the Data Status column needs, and feeding it device.start_time
  // (the sensor ACTIVATION time) made the column report sensor age instead.
  // null = loading or failed; the column then degrades to "active" rather than
  // alarming, matching how the Clinical Dashboard treats the same absence.
  const { lastReadings } = useDeviceLastReadings()

  // Authoritative pet-id set when a clinic filter is active. null when no
  // filter — caller treats null as "no clinic filter, show everything that
  // passes other filters." See useClinicLinkedPets for the failure mode
  // semantics.
  const {
    petIds: linkedPetIds,
    breakdown: linkedBreakdown,
    total: linkedTotal,
    isLoading: linkedLoading,
    error: linkedError,
  } = useClinicLinkedPets(clinicVetFilter)

  // Fetch all pets
  const {
    data: allPets = [],
    total: totalPets,
    isLoading: petsLoading,
  } = useGetList<Pet>('pets', {
    pagination: { page: 1, perPage: 10000 }, // Fetch all for client-side filtering
    sort: { field: 'createdAt', order: 'DESC' },
    filter: searchQuery ? { q: searchQuery } : {},
    meta: { params: { depth: 2 } },
  })

  // Fetch all devices to know which pets have devices and their latest activity
  const {
    data: allDevices = [],
    total: allDevicesTotal,
    isLoading: devicesLoading,
    error: devicesError,
  } = useGetList<Device>('devices', {
    pagination: { page: 1, perPage: 10000 },
    sort: { field: 'start_time', order: 'DESC' },
  })

  // Process pets:
  //   - filter to the authoritative linked-pet set when a clinic filter is on
  //   - DON'T drop pets without device data anymore — they get a "no_sensor" /
  //     "waiting_sync" tag in the datagrid instead (see Section 5 of the spec)
  //   - sort by latest device activity for pets that have it, with the rest
  //     ordered by createdAt so newly-onboarded-but-no-data pets show up
  //     instead of disappearing to the end of an empty list
  const { filteredPets, petsWithDeviceCount, petLatestActivity, petLastReading } = useMemo(() => {
    if (petsLoading || devicesLoading || metricsCheckLoading) {
      return {
        filteredPets: [],
        petsWithDeviceCount: 0,
        petLatestActivity: new Map<number, number>(),
        petLastReading: new Map<number, number>(),
      }
    }

    // Latest device timestamp per pet — only counts devices that have metrics.
    const petLatestActivity = new Map<number, number>()
    const petLastReading = new Map<number, number>()
    for (const device of allDevices) {
      const petId = typeof device.pet === 'object' ? device.pet.id : device.pet
      if (!petId) continue
      if (devicesWithMetrics && !devicesWithMetrics.has(device.id)) continue
      const deviceTimestamp = device.start_time
        ? device.start_time
        : new Date(device.createdAt).getTime() / 1000
      const currentLatest = petLatestActivity.get(petId) || 0
      if (deviceTimestamp > currentLatest) {
        petLatestActivity.set(petId, deviceTimestamp)
      }

      // Separate from the sort key above ON PURPOSE. `petLatestActivity` is the
      // sensor's activation time and orders the list; it must NOT be read as a
      // last reading. Absent here (no row yet, or the fetch failed) leaves the
      // pet out of the map, which the column reads as "unknown" -> active.
      const seen = lastReadings?.get(device.id)
      if (seen) {
        const seenSeconds = Math.floor(seen.getTime() / 1000)
        const currentSeen = petLastReading.get(petId) || 0
        if (seenSeconds > currentSeen) {
          petLastReading.set(petId, seenSeconds)
        }
      }
    }

    // Filter pass:
    //   - clinic-filter active: keep only pets in the backend's linked set.
    //     This is the SAME set the Clinic Directory's pet_count uses, so
    //     count and list cannot disagree.
    //   - clinic-filter active but the backend call is still loading or
    //     errored: fall back to the old petMatchesVetClinic heuristic so the
    //     page degrades to "show what we can" rather than "show nothing."
    //   - no clinic filter: keep everything.
    const pets = allPets.filter((pet) => {
      // Clinic Team filter first — it is the narrower, more literal question
      // ("assigned to this vet"), and it must not be widened by the clinic
      // filter's linked-set fallback.
      if (assignedVetFilter !== null && !petAssignedToVet(pet, assignedVetFilter)) return false
      if (clinicVetFilter === null) return true
      if (linkedPetIds) return linkedPetIds.has(pet.id)
      return petMatchesVetClinic(pet, clinicVetFilter)
    })

    // Sort: pets with known activity first (desc), then the rest by createdAt
    // desc. This keeps the original "freshest data at the top" ordering for
    // the common case but stops banishing data-less linked pets to the
    // bottom of an empty list.
    const sortedPets = pets.slice().sort((a, b) => {
      const aActivity = petLatestActivity.get(a.id)
      const bActivity = petLatestActivity.get(b.id)
      if (aActivity != null && bActivity != null) return bActivity - aActivity
      if (aActivity != null) return -1
      if (bActivity != null) return 1
      const aCreated = a.createdAt ? new Date(a.createdAt).getTime() : 0
      const bCreated = b.createdAt ? new Date(b.createdAt).getTime() : 0
      return bCreated - aCreated
    })

    return {
      filteredPets: sortedPets,
      petsWithDeviceCount: sortedPets.length,
      petLatestActivity,
      petLastReading,
    }
  }, [
    allPets,
    allDevices,
    petsLoading,
    devicesLoading,
    metricsCheckLoading,
    devicesWithMetrics,
    lastReadings,
    clinicVetFilter,
    assignedVetFilter,
    linkedPetIds,
  ])

  // Apply pagination to the filtered results
  const paginatedPets = useMemo(() => {
    const startIndex = (page - 1) * perPage
    return filteredPets.slice(startIndex, startIndex + perPage)
  }, [filteredPets, page, perPage])

  const isLoading = petsLoading || devicesLoading || metricsCheckLoading || linkedLoading

  // Handle search from GlobalSearchBox
  const handleFiltersChange = (filters: any) => {
    setSearchQuery(filters.q || '')
    setPage(1) // Reset to first page on search
  }

  // Create a list context for the datagrid
  // Using type assertion since we're providing a custom filtered/sorted list
  const listContext = {
    data: paginatedPets,
    total: petsWithDeviceCount,
    isLoading,
    isPending: false as const,
    isFetching: false,
    page,
    perPage,
    setPage,
    setPerPage,
    sort: { field: 'createdAt', order: 'DESC' as const },
    setSort: () => {}, // Sorting is handled by our custom logic
    filterValues: {
      ...(searchQuery ? { q: searchQuery } : {}),
      ...(clinicVetFilter ? { vet: clinicVetFilter } : {}),
      ...(assignedVetFilter ? { assignedVet: assignedVetFilter } : {}),
    },
    setFilters: handleFiltersChange,
    displayedFilters: {},
    showFilter: () => {},
    hideFilter: () => {},
    resource: 'pets',
    selectedIds: [] as number[],
    onSelect: () => {},
    onSelectAll: () => {},
    onToggleItem: () => {},
    onUnselectItems: () => {},
    hasPreviousPage: page > 1,
    hasNextPage: page * perPage < petsWithDeviceCount,
    refetch: () => {},
    error: null as any,
    defaultTitle: 'Pets',
  }

  if (isLoading) {
    return (
      <Card>
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <CircularProgress size={40} />
          <Typography sx={{ ml: 2 }}>{translate('ra.page.loading')}</Typography>
        </Box>
      </Card>
    )
  }

  // Display name for the active clinic filter. The Payload User document
  // we get from /api/users/{id} doesn't expose hospital_name with that
  // exact key (the field is hospitalName in the source schema and not
  // on vetpo's User type), so we tolerate either spelling via an indexed
  // lookup and only need the typed fields after that. Fallback chain:
  // hospitalName / hospital_name → clinic_name → name → email → #id.
  const clinicLabel = clinicUser
    ? ((clinicUser as unknown as Record<string, unknown>).hospitalName as string | undefined) ||
      ((clinicUser as unknown as Record<string, unknown>).hospital_name as string | undefined) ||
      clinicUser.clinic_name?.trim() ||
      clinicUser.name?.trim() ||
      clinicUser.email ||
      `Clinic #${clinicUser.id}`
    : null

  // Data Integrity check: the Clinic Directory said "Linked pets: N" using
  // the same backend source as linkedTotal. If the visible list is shorter
  // than that, the missing pets are unfetchable through /api/pets (most
  // likely because access policy hides them, or they were archived between
  // the two calls). Either way, the clinic shouldn't be left thinking the
  // system "lost" their data — we surface the gap explicitly here AND log a
  // structured warning so monitoring can catch the pattern across calls.
  const integrityMismatch =
    clinicVetFilter !== null && linkedTotal !== null && linkedTotal > petsWithDeviceCount
  if (integrityMismatch) {
    // eslint-disable-next-line no-console -- intentional: feeds monitoring/Sentry
    console.warn('[clinic-data-integrity] mismatch', {
      vetId: clinicVetFilter,
      linkedTotal,
      visible: petsWithDeviceCount,
      missing: linkedTotal - petsWithDeviceCount,
      breakdown: linkedBreakdown,
    })
  }

  return (
    <ListContextProvider value={listContext}>
      {/* Title + the list's one view control. The toggle used to float alone
          at the top of the card, above the search box. */}
      <PageHeader
        title={translate('resources.pets.name', { smart_count: 2 })}
        actions={
          canShowHidden ? (
            <FormControlLabel
              sx={{ mr: 0 }}
              control={
                <Switch
                  checked={showHidden}
                  onChange={(e) => setShowHidden(e.target.checked)}
                  data-testid="show-hidden-toggle"
                />
              }
              label={translate('resources.pets.hide.show_hidden_toggle')}
            />
          ) : undefined
        }
      />
      {/* The clinic-wide pets/devices reads are server-capped (ada-core
          DEVICES_MAX_LIMIT), and a pet whose devices fall past the cap renders
          as if it had none. Same notice the dashboard shows — one component so
          the two screens cannot disagree about the same truncation. */}
      <DataTruncationNotice
        lists={[
          { resource: 'pets', loaded: allPets.length, total: totalPets },
          { resource: 'devices', loaded: allDevices.length, total: allDevicesTotal },
        ]}
      />
      <Card>
        {assignedVetFilter !== null && (
          <Box sx={{ px: 2, pt: 2 }}>
            <Chip
              label={translate('pos.clinic_team.view_patients_chip', {
                vet:
                  assignedVetUser?.name?.trim() ||
                  assignedVetUser?.email ||
                  `#${assignedVetFilter}`,
              })}
              onDelete={clearClinicFilter}
              color="primary"
              variant="outlined"
              size="small"
            />
            {petsWithDeviceCount === 0 && !isLoading && (
              <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
                {translate('pos.clinic_team.no_assigned_patients')}
              </Typography>
            )}
          </Box>
        )}
        {clinicVetFilter !== null && (
          <Box sx={{ px: 2, pt: 2 }}>
            <Chip
              label={translate('resources.pets.clinic_filter.chip', {
                name: clinicLabel ?? `#${clinicVetFilter}`,
              })}
              onDelete={clearClinicFilter}
              color="primary"
              variant="outlined"
              size="small"
            />
            {linkedError && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                {translate(linkedError, { _: linkedError })}{' '}
                {translate('resources.pets.clinic_filter.best_effort')}
              </Alert>
            )}
            {integrityMismatch && (
              <Alert
                severity="warning"
                sx={{ mt: 1.5 }}
                data-testid="clinic-data-integrity-warning"
              >
                <Typography variant="body2" component="div" sx={{ fontWeight: 'bold' }}>
                  {translate('resources.pets.clinic_filter.mismatch_title')}
                </Typography>
                <Typography variant="body2" component="div">
                  {translate('resources.pets.clinic_filter.mismatch_body', {
                    linked: linkedTotal,
                    found: petsWithDeviceCount,
                  })}
                </Typography>
                {linkedBreakdown && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    component="div"
                    sx={{ mt: 0.5 }}
                  >
                    {translate('resources.pets.clinic_filter.sources', {
                      direct: linkedBreakdown.directOwner,
                      vets: linkedBreakdown.ownerVets,
                      share: linkedBreakdown.allPetsShare,
                      specific: linkedBreakdown.specificShare,
                    })}
                  </Typography>
                )}
              </Alert>
            )}
            {petsWithDeviceCount === 0 && !isLoading && !integrityMismatch && (
              <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
                {translate('resources.pets.clinic_filter.none_linked')}
              </Typography>
            )}
          </Box>
        )}
        {showHidden && canShowHidden ? (
          <HiddenPetsPanel />
        ) : (
          <>
            <GlobalSearchBox />
            {/* A devices list that FAILED arrives as []: every row's Data Status
                derived 'no_sensor'. Pass no device context instead (PetDatagrid
                then hides the column) and say why (eng-brain pass 78). */}
            {devicesError && (
              <Alert
                severity="warning"
                sx={{ mx: 2, mb: 1 }}
                data-testid="pets-devices-load-failed"
              >
                {translate('resources.pets.data_status_alert.devices_load_failed.title')}{' '}
                {translate('resources.pets.data_status_alert.devices_load_failed.body')}
              </Alert>
            )}
            <PetDatagrid
              rowClick="show"
              devicesWithMetrics={devicesWithMetrics}
              allDevices={devicesError ? undefined : allDevices}
              petLastReading={petLastReading}
            />
            <Pagination />
          </>
        )}
      </Card>
    </ListContextProvider>
  )
}

export default PetList
